import { createContext, ReactNode, useEffect, useReducer } from 'react'
// utils
import apiClient from 'helpers/api-client'
import { isValidToken, setSession } from 'utils/jwt'
// @types
import {
  ActionMap,
  AuthState,
  AuthUser,
  AuthContextType,
  Types,
} from '~/@types/auth'
import { DataResponse } from '~/@types/commons'
import {
  CreatorClass,
  UserProfile,
  UserTypes,
} from '~/@types/profile'
// routes
import { API_USER } from 'routes/apis'
// configs
import isEmpty from 'helpers/is-empty'
import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths'
import { PATH_AFTER_LOGIN } from '~/config'
import router, { useRouter } from 'next/router'
import { getPreviousURL, getUrlQueryParam } from 'helpers/routing'
import { addBreadcrumb, sendLog } from 'helpers/log'
import {
  isCaptchaCancellation,
  isCaptchaFailure,
} from 'helpers/captcha/contracts'
import type { GuestOtpRunner } from 'hooks/domains/Users/useGuestOtpChallenge'
import {
  DeliveryMethod,
  VerificationAction,
} from 'domains/Users/Auth/store/Auth/interface'
import {
  destroySessionStorage,
  setSessionStorage,
} from 'helpers/session-storage'
import trackEvent from '~/trackers'
import { useAuthStore } from 'domains/Users/Auth/store'
import { getIdToken } from 'helpers/auth'
import { getStateFromSource, setUserLogin } from 'helpers/analytics'
import { getDateTimeUTC } from 'helpers/date-time'
import { setUserDevice } from 'helpers/user-device'
import {
  destroyLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from 'helpers/local-storage'
import authConfig from 'configs/auth'
import { useBoundVerificationUserStore } from 'domains/Users/Verification/stores'
import { resetMyBalanceStore } from 'domains/Balance/store/MyBalance'
import { useUserKYCTier3 } from 'domains/Users/Verification/hooks/useVerificationCoin'
import { IStatusTierThree } from 'domains/Users/Verification/stores/VerificationCoin/interface'
import { renderErrorMessage } from 'helpers/auth/error-auth'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'
import { getMyPermissions } from '~/services/team'
import { myPermissionsQueryKey } from 'constants/team'
import { GrantedActions } from '~/services/team/interface'
import redirect from 'helpers/redirect'
import { MAIN_APP_URL } from 'constants/index'
import { useActiveOrgStore } from '~/domains/Organization/store'
import { FEATURE_FLAGS } from 'constants/feature-flags'

// ----------------------------------------------------------------------

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean
    user: AuthUser
  }
  [Types.Login]: {
    user: AuthUser
  }
  [Types.LoginPhone]: {
    user: AuthUser
    action: string
    verificationMethod: DeliveryMethod
  }
  [Types.LoginSocial]: {
    user: AuthUser
  }
  [Types.Logout]: undefined
  [Types.Register]: {
    user: AuthUser
  }
  [Types.Authenticated]: {
    user: AuthUser
  }
}

export type JWTActions =
  ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>]

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isOtp: false,
  user: null,
}

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        isOtp: false,
        user: action.payload.user,
      }
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        isInitialized: true,
        isOtp: false,
        user: action.payload.user,
      }
    case 'LOGIN_PHONE':
      return {
        ...state,
        ...action.payload,
        isAuthenticated: false,
        isOtp: true,
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        isInitialized: false,
        isOtp: false,
        user: null,
      }
    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        isOtp: false,
        user: action.payload.user,
      }
    case 'AUTHENTICATED':
      return {
        ...state,
        ...action.payload,
        isOtp: false,
        isAuthenticated: true,
      }
    default:
      return state
  }
}

// Stable reference so consumers' dependency lists don't churn before /me lands.
const EMPTY_PERMISSIONS: GrantedActions = []

const AuthContext = createContext<AuthContextType | null>(null)

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode
}

function AuthProvider({ children }: AuthProviderProps) {
  const { pathname, query } = useRouter()
  const trackerData = { pathname, query }
  const [state, dispatch] = useReducer(JWTReducer, initialState)
  const sessionID = getLocalStorage('session_id')
    ? JSON.parse(getLocalStorage('session_id'))
    : ''
  const { verificationMethod, countryPhone } = useAuthStore(
    (state) => ({
      verificationMethod: state.verificationMethod,
      countryPhone: state.countryPhone,
    }),
  )
  // GET /me — who this user is in the active org and what they may do. Fetched
  // once here at the session boundary rather than by each consumer: the
  // provider is the single subscriber, and staleTime keeps it to one request
  // for the whole session. Consumers read it off the context (see
  // hooks/commons/useSession) and never touch the query.
  //
  // Gated on the multiple-roles flag: FlagProvider now wraps AuthProvider
  // (see pages/_app.tsx), so the raw useFlag()/useFlagsStatus() hooks work
  // here — useFeatureFlags() itself can't be used in this file, since it
  // calls useAuth() internally and this IS the provider defining that
  // context (it would be reading its own not-yet-rendered value).
  const isMultipleRolesEnabled = useFlag(FEATURE_FLAGS.multipleRoles)
  const { flagsReady } = useFlagsStatus()
  const queryClient = useQueryClient()
  // Read on every render so it picks the token up as soon as one exists —
  // sign-in stores it before any state dispatch, and the post-login navigation
  // re-renders the provider.
  const idToken = getIdToken()
  const hasUsableToken = !isEmpty(idToken) && isValidToken(idToken)
  const {
    data: myPermissions,
    // isInitialLoading, not isLoading: React Query v4 reports a *disabled*
    // query as status 'loading', so isLoading would be stuck true whenever
    // there's no usable token — leaving route guards on a spinner forever.
    isInitialLoading: isLoadingPermissions,
  } = useQuery(
    myPermissionsQueryKey,
    // Wrapped, not by reference: React Query calls queryFn with a
    // QueryFunctionContext, which getMyPermissions would take as its optional
    // orgId and stamp onto the Org-Id header as "[object Object]".
    () => getMyPermissions(),
    {
      // Gated on a usable token AND the flag being known-on. Wait for
      // flagsReady rather than trusting an unready useFlag() (which defaults
      // false) — otherwise this would skip /me on a hard refresh, before
      // Unleash has loaded toggle definitions, even when the flag is on.
      enabled: hasUsableToken && flagsReady && isMultipleRolesEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  )

  const { resetData: resetKycData } = useBoundVerificationUserStore()

  const { refetch: fetchKYCTierThree } = useUserKYCTier3()

  const getUserProfile = async (retryCount = 1) => {
    try {
      const headers = {
        Authorization: getIdToken(),
      }
      const response = await apiClient.get<DataResponse<UserProfile>>(
        API_USER.profile.root,
        {
          headers,
        },
      )
      setLocalStorage('userId', {
        id: response.data?.data.user_id,
      })
      return Promise.resolve(response.data)
    } catch (error) {
      if (
        error?.code === 'POST_REGISTRATION_INCOMPLETE' ||
        error?.response?.data?.code === 'POST_REGISTRATION_INCOMPLETE'
      ) {
        redirect(
          `${MAIN_APP_URL}/user/post-registration?source=${getStateFromSource(
            pathname,
          )}`,
        )
      }
      if (
        retryCount > 0 &&
        (error?.message === 'Network Error' ||
          error?.code === 'ERR_NETWORK' ||
          !error?.response)
      ) {
        return getUserProfile(retryCount - 1)
      }
      return Promise.reject(error?.message || error)
    }
  }

  const getUserKYCTier3 = async () => {
    try {
      const response = await fetchKYCTierThree<IStatusTierThree>()
      return Promise.resolve(response.data)
    } catch (error) {
      return Promise.reject(error?.message || error)
    }
  }

  const userKYCTier3 = async () => {
    const kycTierThree = await getUserKYCTier3()
    dispatch({
      type: Types.Initial,
      payload: {
        isAuthenticated: true,
        user: {
          ...state.user,
          kyc_tier_three: kycTierThree,
        },
      },
    })
  }

  const mappingRoleName = (role: CreatorClass) => {
    switch (role) {
      case 'CREATOR':
        return 'Kreator'
      case 'BUSINESS':
        return 'Badan Usaha'
      case 'PERSONAL':
      default:
        return 'Personal'
    }
  }

  const doUserAuthenticated = async () => {
    try {
      const { data: user } = await getUserProfile()
      if (!user) {
        throw new Error('User profile is empty')
      }

      let kycTierThree = null
      try {
        kycTierThree = await getUserKYCTier3()
      } catch (error) {
        addBreadcrumb('auth', 'getUserKYCTier3 failed', 'warning', {
          error,
        })
      }

      // Guard `creator` — a team member invited into an org has no creator
      // profile of their own, and an unguarded read here throws, which the
      // catch below would turn into a silent never-authenticated state.
      const displayName =
        user?.creator?.name || user.username || 'User'
      // The legacy feature whitelist (GET /hub/v1/role) is NOT fetched here:
      // it's only needed while the multiple-roles flag is off, and this
      // provider sits outside FlagProvider so it can't tell. useSession owns
      // that call and gates it on the flag.
      trackEvent.auth('sign_in_completed', {
        ...trackerData,
        verificationMethod,
      })

      setUserLogin(user.user_id, {
        user_id: user.user_id,
        user_type: user?.creator?.creator_class,
        $email: user.email,
        $avatar: user.avatar,
        $name: user.username,
        phone_number: user.phone,
        gender: user.gender,
        birth_date:
          user?.dob
            ? getDateTimeUTC(new Date(user.dob).toISOString())
            : '',
        creator_name: user?.creator?.name || '',
        creator_status: user?.creator?.status || '',
        creator_category_id: user?.creator?.category || '',
        creator_category: user?.creator?.category_name || '',
      })
      setUserDevice()
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: true,
          user: {
            displayName,
            photoURL: user.avatar,
            role: UserTypes.Creator,
            kyc_tier_three: kycTierThree,
            roleName: mappingRoleName(user?.creator?.creator_class),
            ...user,
          },
        },
      })
    } catch (error) {
      if (
        error === 'user is inactivated' ||
        error?.message === 'user is inactivated' ||
        error?.response?.data?.code === 'USER_IS_SUSPENDED' ||
        error?.response?.data?.message === 'user is inactivated'
      ) {
        logout()
        return
      }

      addBreadcrumb('auth', 'doUserAuthenticated failed', 'error', {
        error,
      })
      sendLog(error)

      setSession(null)
      resetDataAfterLogout()
      if (pathname !== PATH_AUTH.login) {
        router.push(PATH_AUTH.login)
      }

      throw error
    }
  }

  const handleCheckSSO = async () => {
    try {
      const response = await apiClient.get(
        API_USER.auth.getSSOToken + `/${sessionID}`,
      )
      if (response?.data?.code === 'SUCCESS') {
        const token = response?.data?.data?.id_token
        const refreshToken = response?.data?.data?.refresh_token
        setSession(token)
        destroyLocalStorage('session_id')
        setLocalStorage(authConfig.refreshTokenName, refreshToken)
        await doUserAuthenticated()
      }
    } catch (error) {
      addBreadcrumb(
        'auth',
        'auth listen Get SSO Token',
        'error',
        error,
      )
      sendLog(error)
    }
  }

  /**
   * Requests a sign-in OTP for a typed phone number.
   *
   * @param runGuestOtp - Sends the request under the CAPTCHA challenge. Its
   *   cancellation and failure errors are rethrown for the caller to
   *   present and are not logged here.
   */
  const loginPhone = async (
    phone: string,
    method: DeliveryMethod,
    runGuestOtp: GuestOtpRunner,
    action = 'SIGN_IN',
  ) => {
    const phoneNumber = `${countryPhone.dial_code}${+phone}`
    const body = {
      phone_number: phoneNumber,
      action,
      delivery_method: method,
    }
    setSessionStorage('authMethod', 'phone_number')
    try {
      const response = await runGuestOtp(
        (headers) =>
          apiClient.post(
            API_USER.auth.requestOtpByPhone,
            body,
            headers ? { headers } : undefined,
          ),
        { phoneNumber },
      )
      if (response?.data?.code === 'SUCCESS') {
        dispatch({
          type: Types.LoginPhone,
          payload: {
            action,
            verificationMethod: method,
            user: {
              phoneNumber,
            },
          },
        })
      }
    } catch (error) {
      if (!isCaptchaCancellation(error) && !isCaptchaFailure(error)) {
        sendLog(error)
      }
      return Promise.reject(error)
    }
  }

  /**
   * Team-invite accept flow: the phone isn't known client-side yet — BE
   * resolves it from the invitation_token (URL param from the invite email/
   * WhatsApp link) and returns it, same as loginPhone's own phoneNumber.
   *
   * @param runGuestOtp - Same contract as loginPhone's. The challenge is
   *   bound to the invitation token because the number is unknown here.
   */
  const loginPhoneByInvitation = async (
    invitationToken: string,
    method: DeliveryMethod,
    runGuestOtp: GuestOtpRunner,
  ) => {
    const action = 'SIGN_IN'
    const body = {
      invitation_token: invitationToken,
      action,
      delivery_method: method,
    }
    setSessionStorage('authMethod', 'phone_number')
    try {
      const response = await runGuestOtp(
        (headers) =>
          apiClient.post(
            API_USER.auth.requestOtpByPhone,
            body,
            headers ? { headers } : undefined,
          ),
        { invitationToken },
      )
      if (response?.data?.code === 'SUCCESS') {
        // Field name unconfirmed against a real response — verify with BE.
        const phoneNumber = response?.data?.data?.phone_number
        dispatch({
          type: Types.LoginPhone,
          payload: {
            action,
            verificationMethod: method,
            user: {
              phoneNumber,
            },
          },
        })
      }
    } catch (error) {
      if (!isCaptchaCancellation(error) && !isCaptchaFailure(error)) {
        sendLog(error)
      }
      return Promise.reject(error)
    }
  }

  const login = async (email: string, password: string) => {
    setSessionStorage('authMethod', 'email')
    destroySessionStorage('userPhoneNumber')

    setSessionStorage('userEmail', email)

    try {
      const response = await apiClient.post(
        API_USER.auth.signInByEmail,
        {
          email: email,
          password: password,
        },
      )
      if (response?.data?.code === 'SUCCESS') {
        const { id_token: token, refresh_token: refreshToken } =
          response.data.data
        setSession(token)
        setLocalStorage(authConfig.refreshTokenName, refreshToken)
        await doUserAuthenticated()
      }
    } catch (error) {
      sendLog(error)
      return Promise.reject(error)
    }
  }

  const loginSocial = async (provider: string) => {
    try {
      setSessionStorage('authMethod', 'google')
      const response = await apiClient.get(
        API_USER.auth.signInBySSO + `/${provider}`,
      )
      if (response?.data?.code === 'SUCCESS') {
        setLocalStorage(
          'session_id',
          response?.data?.data?.session_id,
        )
        redirect(response?.data?.data?.url)
      }
    } catch (error) {
      addBreadcrumb('auth', 'auth listen signIn_failure', 'error', {
        error,
      })
      sendLog(error)
    }
  }

  const verify = async (
    otp: string,
    delivery_method: DeliveryMethod,
    username: string,
    action = 'SIGN_IN',
    invitationToken?: string,
  ) => {
    try {
      const response = await apiClient.post(API_USER.auth.verifyOtp, {
        phone_number: username,
        action,
        delivery_method,
        otp: Number(otp),
        ...(invitationToken
          ? { invitation_token: invitationToken }
          : {}),
      })
      if (response?.data?.code === 'SUCCESS') {
        try {
          const loginResponse = await apiClient.post(
            API_USER.auth.signInByPhone,
            {
              phone_number: username,
              is_new_user: false,
              ...(invitationToken
                ? { invitation_token: invitationToken }
                : {}),
            },
          )
          if (loginResponse?.data?.code === 'SUCCESS') {
            const { id_token: token, refresh_token: refreshToken } =
              loginResponse.data.data
            setSession(token)
            await doUserAuthenticated()
            setLocalStorage(authConfig.refreshTokenName, refreshToken)
          }
        } catch (error) {
          sendLog(error)
          return Promise.reject(error)
        }
      }
    } catch (error) {
      sendLog(error)
      return Promise.reject(error)
    }
  }

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => {
    const response = await apiClient.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    })
    const { accessToken, user } = response.data
    setSession(accessToken)

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    })
  }

  const resetDataAfterLogout = () => {
    destroySessionStorage('hideQRCodeAlert')
    setSession(null)
    dispatch({ type: Types.Logout })
    resetKycData()
    resetMyBalanceStore()
    useBoundVerificationUserStore.persist.clearStorage()
    // Drop the WHOLE cache, not just /me. Two reasons, both bugs we hit:
    //   1. `staleTime: Infinity` means an invalidated-but-still-cached result
    //      is handed back as-is the instant `enabled` flips true on the next
    //      login, with no new request.
    //   2. React Query serves the last SUCCESSFUL `data` when a refetch
    //      fails. So any query left cached here (the organization list, the
    //      dashboard summaries, …) keeps rendering the previous account's
    //      data if its refetch 403s after re-login — e.g. the org switcher
    //      listing orgs that belong to whoever was signed in before.
    // Nothing cached under the old session should outlive it.
    //
    // Before clearActiveOrg below, not after: useActiveOrg seeds the active
    // org from the cached list whenever none is stored, so clearing in the
    // other order leaves a window where the just-cleared id gets seeded
    // straight back from the previous account's list.
    queryClient.clear()
    // Drop the active org so the next login in this browser doesn't inherit
    // it — the api-client interceptor reads this straight from persisted
    // storage, so a stale id here would stamp the wrong Org-Id header for
    // whoever logs in next.
    useActiveOrgStore.getState().clearActiveOrg()
  }

  const logout = async () => {
    try {
      await apiClient.post(API_USER.auth.signOut, {
        refresh_token: getLocalStorage(authConfig.refreshTokenName),
      })
    } catch (error) {
      // Revoking the session server-side is best-effort. Local teardown has
      // to happen either way: this used to sit in a `.then()`, so any failure
      // here (network error, already-expired refresh token, 500, timeout)
      // left the token, the active org id AND the whole query cache in
      // place. The next login in this browser then inherited the previous
      // account's Org-Id and 403'd on every org-scoped request — including
      // GET /organizations itself, which is what recovery depends on.
      sendLog(error)
    } finally {
      resetDataAfterLogout()
    }
  }

  const claimToken = async (tokenId: string) => {
    setSession(null)
    try {
      const response = await apiClient.post(
        API_USER.auth.tokenClaim,
        {
          token_id: tokenId,
        },
      )
      const { auth_token_id: authTokenId = '' } = response.data.data
      if (!authTokenId) return Promise.reject('Invalid token')
      setSession(authTokenId)
      await doUserAuthenticated()
      return Promise.resolve(authTokenId)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const getRedirectUrl = () => {
    if (!state.isAuthenticated) return PATH_AUTH.login
    // Same destination for every role — login already sends everyone here.
    const previousUrl = getPreviousURL()
    return previousUrl === '/' || previousUrl === PATH_DASHBOARD.root
      ? PATH_AFTER_LOGIN
      : previousUrl
  }

  const sendOTP = async (
    phone: string,
    method: DeliveryMethod,
    action: VerificationAction = 'SIGN_IN',
  ) => {
    const phoneNumber = `${countryPhone.dial_code}${+phone}`
    try {
      const response = await apiClient.post(
        API_USER.auth.requestHubOtp,
        {
          username: phoneNumber,
          action,
          delivery_method: method,
        },
      )
      if (response?.data?.code === 'SUCCESS') {
        return Promise.resolve(response)
      }
    } catch (error) {
      sendLog(error)
      const errorMessage = renderErrorMessage(
        error?.errors?.[0]?.err || error?.code,
        {
          seconds: error.data?.retry_in_secs,
          defaultMessage: error.message,
        },
      )
      return Promise.reject(errorMessage)
    }
  }

  const forceLogout = async () => {
    setSession(null)
    resetDataAfterLogout()
    router.push(PATH_AUTH.login)
  }

  const initialize = async () => {
    const idToken = getIdToken()
    // A team-invite link lands here with no (or a stale) login token by
    // design — don't bounce to /auth/login in that case and let the
    // invite_token flow (VerifyCodeForm -> loginPhoneByInvitation) take
    // over instead. Read straight off the browser URL (not next/router's
    // query) since this runs in a one-time mount effect, before the router
    // is guaranteed to have finished hydrating query.
    const isInvitationFlow =
      pathname === PATH_AUTH.verify &&
      !isEmpty(getUrlQueryParam('invite_token'))

    if (!isEmpty(idToken)) {
      // try {
      //   setSession(idToken)
      //   // const { code } = await getUserProfile()
      //   // if (code === 'SUCCESS') {
      //   await doUserAuthenticated()
      //   // }
      // } catch (error) {
      if (idToken && isValidToken(idToken)) {
        setSession(idToken)
        try {
          const authenticating = doUserAuthenticated()
          // Already signed in on this device — no need to run the OTP
          // flow again (invite link or otherwise). Send them to the
          // dashboard gateway instead of leaving them stuck on this page.
          // Await first: '/' resolves its target from `isAuthenticated`, so
          // redirecting before that lands bounces the user to /auth/login.
          if (pathname === PATH_AUTH.verify) {
            await authenticating
            router.push('/')
          } else {
            await authenticating
          }
        } catch {
          // Teardown and login redirect are handled by doUserAuthenticated.
        }
      } else {
        setSession(null)
        if (!isInvitationFlow) {
          router.push(PATH_AUTH.login)
        }
      }
      // }
    } else if (!isInvitationFlow) {
      forceLogout()
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  useEffect(() => {
    if (sessionID) {
      handleCheckSSO()
    }
  }, [sessionID])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginPhone,
        loginPhoneByInvitation,
        login,
        loginSocial,
        logout,
        register,
        verify,
        claimToken,
        targetRedirectUrl: getRedirectUrl(),
        getUserProfile,
        getUserKYCTier3,
        userKYCTier3,
        sendOTP,
        doUserAuthenticated,
        orgId: myPermissions?.org_id ?? '',
        orgName: myPermissions?.org_name ?? '',
        orgRole: myPermissions?.role ?? '',
        permissions: myPermissions?.permissions ?? EMPTY_PERMISSIONS,
        isLoadingPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
